/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { faLink } from '@fortawesome/free-solid-svg-icons/faLink'
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons/faPlusCircle'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import SearchInput, {
  SearchInputOption,
  SearchInputRenderOption
} from '../../../../client/js/components/form/fields/SearchInput'
import { fetchCustomer, findManyCustomers } from '../../lib/rest/customerAPIClient'
import { useQuery } from '@tanstack/react-query'
import { getHalEmbedded } from '@jalik/hal'
import { getCustomerName } from '../../lib/customer.mjs'
import { getCustomerCreateLink, getCustomerLink } from '../../lib/links/customerLinks'
import { ActionIcon, AutocompleteProps, Box } from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'

function CustomerOption (props: SearchInputRenderOption<Customer>) {
  const { option } = props
  return (
    <Box w="100%">
      {getCustomerName(option.item)}
    </Box>
  )
}

export type CustomerInputProps = AutocompleteProps & {
  queryFilter?: Record<string, any>,
}

function CustomerInput (props: CustomerInputProps) {
  const intl = useIntl()
  const {
    disabled,
    label,
    id,
    name = 'customerId',
    onChange,
    queryFilter,
    required,
    value,
    ...others
  } = props

  const fieldId = id || `${name}-field`

  const [searchParams, setSearchParams] = useState({ limit: 5, search: null })

  const listQuery = useQuery({
    enabled: true,
    queryKey: ['customers', queryFilter, searchParams],
    queryFn: () => findManyCustomers({
      ...queryFilter,
      ...searchParams
    }),
    refetchOnWindowFocus: false
  })

  const data = useMemo(() => (
    (listQuery.data != null ? getHalEmbedded(listQuery.data)?.customers : null) || []
  ), [listQuery.data])

  const options: SearchInputOption<Customer>[] = useMemo(() => (
    data.map((el) => ({
      item: el,
      label: getCustomerName(el),
      value: String(el.id)
    }))
  ), [data])

  const setSearch = useCallback((search: string) => {
    setSearchParams((s) => ({ ...s, search }))
  }, [])

  const customerQuery = useQuery({
    enabled: value != null && value !== '',
    queryKey: ['customer', value],
    queryFn: () => fetchCustomer(parseInt(value, 10)),
    refetchOnWindowFocus: false
  })
  const { data: customer } = customerQuery

  useEffect(() => {
    if (customer != null && searchParams.search == null) {
      setSearchParams((s) => ({
        ...s, search: getCustomerName(customer)
      }))
    }
  }, [customer, searchParams.search])

  return (
    <SearchInput
      id={fieldId}
      label={label || intl.formatMessage({
        id: 'customer',
        defaultMessage: 'Client'
      })}
      {...others}
      data={options}
      disabled={disabled}
      name={name}
      onChange={onChange}
      onSearchChange={setSearch}
      renderOption={CustomerOption}
      required={required}
      searchValue={searchParams.search}
      value={value != null ? String(value) : null}
      rightSection={<>
        {value == null
          ? <ActionIcon
            component={AnchorLink}
            target="_blank"
            title={intl.formatMessage({
              id: 'new',
              defaultMessage: 'Nouveau'
            })}
            to={getCustomerCreateLink()}
            variant="subtle"
          >
            <FontAwesomeIcon
              fixedWidth
              icon={faPlusCircle}
            />
          </ActionIcon>
          : <ActionIcon
            disabled={!value}
            component={AnchorLink}
            target="_blank"
            to={getCustomerLink(Number(value))}
            variant="subtle"
          >
            <FontAwesomeIcon
              fixedWidth
              icon={faLink}
            />
          </ActionIcon>
        }
      </>}
    />
  )
}

export default CustomerInput
